import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import Portal from '@atlaskit/portal';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	type DismissEvent,
	type Placement,
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	UNSAFE_UpdateOnChange as SpotlightUpdateOnChange,
} from '@atlaskit/spotlight';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import SplitButtonGif from '../../../assets/SplitButton.gif';

import { useCwRSpotlightSeenStorage } from './useCwRSpotlightSeenStorage.tsx';
import { useElementBySelector } from './useElementBySelector.ts';

type UniversalCreateCwRSpotlightOnboardingKey =
	(typeof UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS)[keyof typeof UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS];
const ONBOARDING_STATE_KEYS_TO_FETCH = Object.values(
	UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS,
) as UniversalCreateCwRSpotlightOnboardingKey[];

const ANALYTICS_SOURCE = 'universalCreateCwRExperimentSpotlight';

const spotlightAnchorStyles = cssMap({
	// Anchor for the dropdown chevron spotlight.
	menuAnchorWrapper: {
		display: 'block',
		position: 'absolute',
		top: token('space.400'),
		left: token('space.negative.100'),
		width: '1px',
		height: '1px',
	},
	// Anchor for the primary "Create" button spotlight.
	buttonAnchorWrapper: {
		display: 'block',
		position: 'absolute',
		bottom: token('space.negative.100'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: '1px',
		height: '1px',
	},
});

const RICH_TEXT_VALUES = {
	b: (chunks: React.ReactNode) => <Text weight="bold">{chunks}</Text>,
};

export type UniversalCreateSpotlightMessages = {
	title: MessageDescriptor;
	bodyLine1: MessageDescriptor;
	bodyLine2: MessageDescriptor;
	cta: MessageDescriptor;
};

export type UseRenderCwRSpotlightArgs = {
	anchorSelector: string;
	choreographerMessageId: string;
	messages: UniversalCreateSpotlightMessages;
	placement: Placement;
	isEnabled: boolean;
	requireAnchorForStart?: boolean;
	onCtaClick?: () => void;
	onClosed?: () => void;
};

export type UseRenderCwRSpotlightResult = {
	spotlight: React.ReactNode;
	startSpotlight: () => boolean;
	stopSpotlight: () => void;
	isSpotlightActive: boolean;
};

export const useRenderCwRSpotlight = ({
	anchorSelector,
	choreographerMessageId,
	messages,
	placement,
	isEnabled,
	requireAnchorForStart = false,
	onCtaClick,
	onClosed,
}: UseRenderCwRSpotlightArgs): UseRenderCwRSpotlightResult => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();
	const { useChoreographer } = useChoreographerAssets();
	const {
		hasSeenSpotlight: hasSeenSpotlightInStorage,
		markSpotlightSeen: markSpotlightSeenInStorage,
	} = useCwRSpotlightSeenStorage();

	const isServerPlaceholder = useRenderServerPlaceholder();

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const skipOnboardingStateQuery = hasSeenSpotlightInStorage || !isEnabled || isServerPlaceholder;
	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(ONBOARDING_STATE_KEYS_TO_FETCH, skipOnboardingStateQuery);

	const { hasSeenUniversalCreateCwRExperimentSpotlight } = deserializeState(onboardingStateData);
	const shouldSuppressSpotlight =
		Boolean(hasSeenUniversalCreateCwRExperimentSpotlight) || hasSeenSpotlightInStorage;

	// When the backend onboarding state reports the spotlight has been seen but local storage has
	// not been updated yet, mirror it into local storage so subsequent renders short-circuit the
	// `useGetOnboardingState` query (which is otherwise re-run on every render).
	useEffect(() => {
		if (hasSeenUniversalCreateCwRExperimentSpotlight && !hasSeenSpotlightInStorage) {
			markSpotlightSeenInStorage();
		}
	}, [
		hasSeenUniversalCreateCwRExperimentSpotlight,
		hasSeenSpotlightInStorage,
		markSpotlightSeenInStorage,
	]);

	const anchorEl = useElementBySelector(anchorSelector, {
		disabled: isServerPlaceholder || shouldSuppressSpotlight || Boolean(isOnboardingStateError),
	});

	const hasStoppedRef = useRef<boolean>(false);

	const handleSpotlightStart = useCallback(() => {
		setIsSpotlightActive(true);
		hasStoppedRef.current = false;

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'universalCreateCwRExperimentSpotlight',
				attributes: { source: ANALYTICS_SOURCE },
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const markSpotlightSeen = useCallback(() => {
		markSpotlightSeenInStorage();
		setOnboardingState({
			key: UNIVERSAL_CREATE_CWR_EXPERIMENT_SPOTLIGHT_ONBOARDING_STATE_KEYS.hasSeenUniversalCreateCwRExperimentSpotlight,
			value: 'true',
		});
	}, [markSpotlightSeenInStorage, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const choreographer = useChoreographer(choreographerMessageId, {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const startSpotlight = useCallback(() => {
		if (
			isOnboardingStateLoading ||
			isOnboardingStateError ||
			shouldSuppressSpotlight ||
			(requireAnchorForStart && anchorEl === null)
		) {
			return false;
		}

		void choreographer.startMessage();
		return true;
	}, [
		choreographer,
		shouldSuppressSpotlight,
		requireAnchorForStart,
		isOnboardingStateError,
		isOnboardingStateLoading,
		anchorEl,
	]);

	const stopSpotlight = useCallback(() => {
		if (hasStoppedRef.current) {
			return;
		}
		hasStoppedRef.current = true;
		void choreographer.stopMessage();
	}, [choreographer]);

	const closeSpotlight = useCallback(
		(action: 'clicked' | 'dismissed') => {
			markSpotlightSeen();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'button',
					actionSubjectId: 'universalCreateCwRExperimentSpotlight',
					source: ANALYTICS_SOURCE,
				},
			}).fire();

			stopSpotlight();
			onClosed?.();
		},
		[createAnalyticsEvent, markSpotlightSeen, stopSpotlight, onClosed],
	);

	const handleDismiss = useCallback(
		(_event: DismissEvent) => closeSpotlight('dismissed'),
		[closeSpotlight],
	);

	const handleCtaClick = useCallback(() => {
		closeSpotlight('clicked');
		onCtaClick?.();
	}, [closeSpotlight, onCtaClick]);

	const canRender =
		!isServerPlaceholder &&
		isEnabled &&
		anchorEl !== null &&
		!isOnboardingStateLoading &&
		!shouldSuppressSpotlight;

	const spotlight = canRender ? (
		<PopoverProvider>
			{createPortal(
				<Box
					aria-hidden
					testId="hidden-mounting-target"
					xcss={
						placement === 'left-end'
							? spotlightAnchorStyles.menuAnchorWrapper
							: spotlightAnchorStyles.buttonAnchorWrapper
					}
				>
					<PopoverTarget>
						<Box as="span" />
					</PopoverTarget>
				</Box>,
				anchorEl,
			)}
			<Portal zIndex={600}>
				<PopoverContent dismiss={handleDismiss} isVisible={isSpotlightActive} placement={placement}>
					<SpotlightUpdateOnChange selectors={[anchorSelector]} />
					<SpotlightCard testId="universal-create-cwr-experiment-spotlight">
						<SpotlightHeader>
							<SpotlightHeadline>{formatMessage(messages.title)}</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<Image src={SplitButtonGif} alt="" width={295} height={135} />
						</SpotlightMedia>
						<SpotlightBody>
							<Stack space="space.150">
								<Text>
									<FormattedMessage {...messages.bodyLine1} values={RICH_TEXT_VALUES} />
								</Text>
								<Text>
									<FormattedMessage {...messages.bodyLine2} values={RICH_TEXT_VALUES} />
								</Text>
							</Stack>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleCtaClick}>
									{formatMessage(messages.cta)}
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	) : null;

	return { spotlight, startSpotlight, stopSpotlight, isSpotlightActive };
};
